import {TimeUnit} from "./index.ts";
import Decimal from "decimal.js";

export enum ValueType {
    BOOLEAN = 'BOOLEAN',
    COUNT = 'COUNT',
    DURATION = 'DURATION',
    PERCENT = 'PERCENT',
    QUOTE = 'QUOTE',
    SOL = 'SOL',
    STRING = 'STRING',
    USD = 'USD'
}

export type ValueNumberType = ValueType.COUNT | ValueType.PERCENT | ValueType.SOL | ValueType.QUOTE | ValueType.USD;

export type Value =
    ValueBoolean
    | ValueCount
    | ValueDuration
    | ValuePercent
    | ValueQuote
    | ValueSol
    | ValueString
    | ValueUsd;

export type ValueNumber = ValueCount | ValuePercent | ValueQuote | ValueSol | ValueUsd;

export type ValueBoolean = {
    type: ValueType.BOOLEAN;
    value: boolean;
}

export type ValueCount = {
    type: ValueType.COUNT;
    value: Decimal;
}

export type ValueDuration = {
    type: ValueType.DURATION;
    value: number;
    unit: TimeUnit;
}

const durationToSeconds = (duration: ValueDuration): number => {
    const conversionFactors: Record<TimeUnit, number> = {
        [TimeUnit.SECOND]: 1,
        [TimeUnit.MINUTE]: 60,
        [TimeUnit.HOUR]: 3600,
        [TimeUnit.DAY]: 86400,
    };

    return duration.value * conversionFactors[duration.unit];
}

export const compareDurations = (a: ValueDuration, b: ValueDuration): number => {
    const aSeconds = durationToSeconds(a);
    const bSeconds = durationToSeconds(b);
    return aSeconds - bSeconds;
}


export type ValuePercent = {
    type: ValueType.PERCENT;
    value: Decimal;
}

export type ValueQuote = {
    type: ValueType.QUOTE;
    value: Decimal;
}

export type ValueSol = {
    type: ValueType.SOL;
    value: Decimal;
}

export type ValueString = {
    type: ValueType.STRING;
    value: string;
}

export type ValueUsd = {
    type: ValueType.USD;
    value: Decimal;
}


export const isValueNumber = (obj: any): obj is ValueNumber => {
    const valueNumberTypes = ['COUNT', 'PERCENT', 'SOL', 'QUOTE', 'USD'];
    return (
        obj &&
        typeof obj === 'object' &&
        typeof obj.type === 'string' &&
        valueNumberTypes.includes(obj.type) &&
        ('value' in obj)
    );
}


export const deserializeValueNumbers = <T>(obj: T): T => {
    if (Array.isArray(obj)) {
        return obj.map(deserializeValueNumbers) as T;
    }

    if (obj !== null && typeof obj === 'object') {
        if (isValueNumber(obj)) {
            return {
                ...obj,
                value: new Decimal(obj.value),
            };
        }

        const result: any = {};
        for (const key in obj) {
            result[key] = deserializeValueNumbers(obj[key]);
        }
        return result;
    }

    return obj;
}

export const serializeValueNumbers = <T>(obj: T): T => {
    if (Array.isArray(obj)) {
        return obj.map(serializeValueNumbers) as T;
    }

    if (obj !== null && typeof obj === 'object') {
        if (isValueNumber(obj)) {
            return {
                ...obj,
                value: obj.value.toString(),
            };
        }

        const result: any = {};
        for (const key in obj) {
            result[key] = serializeValueNumbers
            (obj[key]);
        }
        return result;
    }

    return obj;
};
